// Profile del management. Datos en MS-Aspect-Users, password en MS-Auth,
// prefs locales (redux). 2FA delega en el Main2fa existente.
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Card, CardBody, FormGroup, Input, Button, Spinner, Label,
} from "reactstrap";
import { translate } from "react-translate";
import { toast } from "react-toastify";
import moment from "moment";
import AuthService from "plugins/TS-lib-utils-public/services/AuthService";
import ManagementProfileService from "services/ManagementProfileService";
import { actionSetUser, actionUpdateLang, actionUpdateDateFormat } from "store/actions/usersActions";
import Main2fa from "components/Users/Main2fa";
import languages from "configs/lang";
import {
  buildInitials, userAvatarBg, userHasPicture, userPictureUrl,
  userFirstName, userLastName, userFullName,
} from "helpers/userAvatar";

const Profile = (props) => {
  const { t } = props;
  const dispatch = useDispatch();
  const user = useSelector((state) => state.userReducer);

  const [firstName, setFirstName] = useState(userFirstName(user));
  const [lastName,  setLastName]  = useState(userLastName(user));
  const [email,     setEmail]     = useState(user.email || "");
  const [savingInfo, setSavingInfo] = useState(false);

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPass, setSavingPass] = useState(false);

  // Sync local state cuando el user cambia en redux (saga puede resolver post-mount)
  useEffect(() => {
    setFirstName(userFirstName(user));
    setLastName(userLastName(user));
    setEmail(user.email || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.isAuth, user.firstName, user.first_name, user.lastName, user.last_name, user.email]);

  const dirtyInfo =
    firstName !== userFirstName(user) ||
    lastName  !== userLastName(user) ||
    email     !== (user.email || "");

  const saveInfo = async (e) => {
    e.preventDefault();
    if (!dirtyInfo) return;
    const authUserId = user.auth_user_id || user._id;
    if (!authUserId) {
      toast.error(t("info-save-error") || "Could not update profile");
      return;
    }
    setSavingInfo(true);
    try {
      const res = await ManagementProfileService.updateProfile(authUserId, {
        firstName, lastName, email,
      });
      if (res && res.status >= 200 && res.status < 300) {
        dispatch(actionSetUser({ ...user, firstName, lastName, email }));
        toast.success(t("info-saved") || "Profile updated");
      } else {
        toast.error(
          (res && res.data && (res.data.message || res.data.error)) ||
          t("info-save-error") || "Could not update profile"
        );
      }
    } catch (err) {
      toast.error(t("info-save-error") || "Could not update profile");
    } finally {
      setSavingInfo(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (!oldPassword || !newPassword) return;
    if (newPassword !== confirmPassword) {
      toast.error(t("password-mismatch") || "Password confirmation does not match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error(t("password-too-short") || "Password must be at least 6 characters");
      return;
    }
    setSavingPass(true);
    try {
      // MS-Auth/UsersController.changePassword espera oldPassword, password, rePassword.
      const res = await AuthService.changePassword({
        oldPassword,
        password: newPassword,
        rePassword: confirmPassword,
      });
      if (res && res.status === 200) {
        toast.success(t("password-saved") || "Password changed");
        setOldPassword(""); setNewPassword(""); setConfirmPassword("");
      } else {
        toast.error(
          (res && res.data && (typeof res.data === "string" ? res.data : res.data.message)) ||
          t("password-save-error") || "Could not change password"
        );
      }
    } catch {
      toast.error(t("password-save-error") || "Could not change password");
    } finally {
      setSavingPass(false);
    }
  };

  const handleLang = (e) => dispatch(actionUpdateLang(e.target.value));
  const handleDateFormat = (e) => dispatch(actionUpdateDateFormat(e.target.value));

  const fullName = userFullName(user) || user.username || "—";

  return (
    <div className="content profile-page mt-3">
      {/* Hero */}
      <Card className="profile-hero">
        <CardBody>
          <div className="profile-hero-flex">
            <div
              className="profile-hero-avatar"
              style={!userHasPicture(user) ? { background: userAvatarBg(user) } : undefined}
            >
              {userHasPicture(user) ? (
                <img src={userPictureUrl(user)} alt={userFirstName(user) || "User"} />
              ) : (
                <span>{buildInitials(user)}</span>
              )}
            </div>
            <div className="profile-hero-info">
              <h2 className="profile-hero-name">{fullName}</h2>
              {user.email && <div className="profile-hero-email">{user.email}</div>}
              {user.username && (
                <div className="profile-hero-meta">
                  <span className="profile-hero-label">{t("username") || "Username"}</span>
                  <code>{user.username}</code>
                </div>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Personal info */}
      <Card className="profile-section">
        <CardBody>
          <div className="profile-section-header">
            <i className="now-ui-icons users_circle-08" />
            <h5>{t("personal-info") || "Personal information"}</h5>
          </div>
          <form onSubmit={saveInfo} className="profile-form-grid">
            <FormGroup>
              <Label>{t("first-name") || "First name"}</Label>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </FormGroup>
            <FormGroup>
              <Label>{t("last-name") || "Last name"}</Label>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </FormGroup>
            <FormGroup className="profile-form-wide">
              <Label>{t("email") || "Email"}</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </FormGroup>
            <div className="profile-form-actions">
              <Button color="primary" type="submit" disabled={!dirtyInfo || savingInfo}>
                {savingInfo ? <Spinner size="sm" /> : <><i className="now-ui-icons ui-1_check" /> {t("save") || "Save"}</>}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      {/* Preferences (idioma + date format) */}
      <Card className="profile-section">
        <CardBody>
          <div className="profile-section-header">
            <i className="now-ui-icons ui-2_settings-90" />
            <h5>{t("preferences") || "Preferences"}</h5>
          </div>
          <div className="profile-form-grid">
            <FormGroup>
              <Label>{t("language") || "Language"}</Label>
              <Input
                type="select"
                name="language"
                defaultValue={user.language}
                onChange={handleLang}
              >
                {Object.keys(languages).map((val) => (
                  <option key={val} value={val}>{languages[val].name}</option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label>{t("date-format") || "Date format"}</Label>
              <Input
                type="select"
                name="dateFormat"
                defaultValue={user.dateFormat}
                onChange={handleDateFormat}
              >
                <option value="DD.MM.YYYY">DD.MM.YYYY ({moment().format("DD.MM.YYYY")})</option>
                <option value="MM-DD-YYYY">MM-DD-YYYY ({moment().format("MM-DD-YYYY")})</option>
                <option value="DD-MM-YYYY">DD-MM-YYYY ({moment().format("DD-MM-YYYY")})</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD ({moment().format("YYYY-MM-DD")})</option>
              </Input>
            </FormGroup>
          </div>
        </CardBody>
      </Card>

      {/* Change password */}
      <Card className="profile-section">
        <CardBody>
          <div className="profile-section-header">
            <i className="now-ui-icons ui-1_lock-circle-open" />
            <h5>{t("change-password") || "Change password"}</h5>
          </div>
          <form onSubmit={savePassword} className="profile-form-grid">
            <FormGroup className="profile-form-wide">
              <Label>{t("current-password") || "Current password"}</Label>
              <Input type="password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} autoComplete="current-password" />
            </FormGroup>
            <FormGroup>
              <Label>{t("new-password") || "New password"}</Label>
              <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} autoComplete="new-password" />
            </FormGroup>
            <FormGroup>
              <Label>{t("confirm-password") || "Confirm new password"}</Label>
              <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} autoComplete="new-password" />
            </FormGroup>
            <div className="profile-form-actions">
              <Button
                color="primary"
                type="submit"
                disabled={!oldPassword || !newPassword || !confirmPassword || savingPass}
              >
                {savingPass ? <Spinner size="sm" /> : <><i className="now-ui-icons ui-1_check" /> {t("update-password") || "Update password"}</>}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      {/* 2FA */}
      <Main2fa />
    </div>
  );
};

export default translate("profile")(Profile);
